<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import { computed, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'

defineOptions({ layout: AppLayout })

const props = defineProps({
  account:    { type: Object, default: () => ({}) },
  owners:     { type: Array,  default: () => [] },
  account_id: { type: Number, default: null }
})

const OWNER_TYPE_LABELS = {
  VehicleSource: 'Supplier',
  Driver: 'Driver',
  TourVendor: 'Tour vendor',
  PropertyHost: 'Property host',
}

const form = useForm({
  owner_type: props.account.owner_type || 'VehicleSource',
  owner_id:   props.account.owner_id   || '',
  email:      props.account.email      || ''
})

const isEdit = !!props.account_id

const filteredOwners = computed(() =>
  props.owners.filter(o => o.type === form.owner_type)
)

const ownerPlaceholder = computed(() => {
  const label = OWNER_TYPE_LABELS[form.owner_type] || form.owner_type
  return `— select a ${label.toLowerCase()} —`
})

function onOwnerTypeChange() {
  form.owner_id = ''
}

// Autofill email from the owner card (VehicleSource.contact_email,
// TourVendor.contact_email). Only fills when email is empty — never
// clobbers an address the operator already typed. Drivers don't carry
// an email column so the autofill is a no-op for them.
watch(() => form.owner_id, (id) => {
  if (!id || form.email) return
  const owner = props.owners.find(o => o.value === id && o.type === form.owner_type)
  if (owner?.email) form.email = owner.email
})

function submit() {
  if (isEdit) {
    form.transform(d => ({ portal_account: { email: d.email } }))
        .patch(`/admin/portal_accounts/${props.account_id}`)
  } else {
    form.transform(d => ({ portal_account: d }))
        .post('/admin/portal_accounts')
  }
}
</script>

<template>
  <PageShell
    :title="isEdit ? 'Edit portal account' : 'New portal account'"
    back-href="/admin/portal_accounts"
    back-label="Portal accounts"
  >
    <div class="form-wrap">
      <form id="portal-account-form" @submit.prevent="submit">
        <ul v-if="account.errors && account.errors.length" class="form-errors">
          <li v-for="e in account.errors" :key="e">{{ e }}</li>
        </ul>

        <div v-if="!isEdit" class="field">
          <label class="field-label">Owner type</label>
          <select v-model="form.owner_type" class="field-input" @change="onOwnerTypeChange">
            <option v-for="(label, type) in OWNER_TYPE_LABELS" :key="type" :value="type">{{ label }}</option>
          </select>
          <span v-if="form.errors.owner_type" class="field-error">{{ form.errors.owner_type }}</span>
        </div>

        <div v-if="!isEdit" class="field">
          <label class="field-label">Owner</label>
          <select v-model="form.owner_id" class="field-input">
            <option value="">{{ ownerPlaceholder }}</option>
            <option v-for="o in filteredOwners" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <span v-if="form.errors.owner_id" class="field-error">{{ form.errors.owner_id }}</span>
        </div>

        <div class="field">
          <label class="field-label">Email</label>
          <input v-model="form.email" type="email" class="field-input" placeholder="supplier@example.com" />
          <span v-if="form.errors.email" class="field-error">{{ form.errors.email }}</span>
        </div>

        <div class="actions">
          <button type="submit" :disabled="form.processing" class="btn-primary">
            {{ isEdit ? 'Save changes' : 'Create account' }}
          </button>
          <a href="/admin/portal_accounts" class="btn-secondary">Cancel</a>
        </div>
      </form>
    </div>
  </PageShell>
</template>

<style scoped>
.form-wrap {
  max-width: 32rem;
}
.form-errors {
  list-style: none;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  border-radius: var(--radius-input, 8px);
  background: color-mix(in oklch, var(--danger) 12%, var(--surface));
  color: var(--danger);
  font-size: 0.875rem;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-bottom: 1.25rem;
}
.field-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--inkSoft);
}
.field-input {
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-input, 8px);
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font-size: 0.875rem;
  outline: none;
}
.field-input:focus {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}
.field-error {
  font-size: 0.75rem;
  color: var(--danger);
}
.actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
}
.btn-primary {
  padding: 0.5rem 1.25rem;
  background: var(--accent);
  color: var(--accentInk);
  border: none;
  border-radius: var(--radius-button, 8px);
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
}
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-secondary {
  padding: 0.5rem 1.25rem;
  background: var(--surfaceAlt);
  color: var(--ink);
  border: 1px solid var(--line);
  border-radius: var(--radius-button, 8px);
  font-size: 0.875rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}
</style>
